import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Calendar, DollarSign, Users, Smartphone, LogOut, Settings, FileText, ListTodo, ClipboardList, Package, PoundSterling } from "lucide-react";
import { WhatsNewButton } from "@/components/custom/WhatsNew";
import VersionPill from "@/components/custom/VersionPill";

const NAV_ITEMS = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/calendar", icon: Calendar, label: "Calendar" },
  { path: "/financials", icon: DollarSign, label: "Financials", adminOnly: true, mobileHidden: true },
  { path: "/quotes-list", icon: ClipboardList, label: "Quotes" },
  { path: "/invoices", icon: FileText, label: "Invoices", adminOnly: true },
  // { path: "/payments", icon: PoundSterling, label: "Payments", adminOnly: true }, // Hidden 26/05/2026 — re-enable by uncommenting. Route still active at /payments; page code preserved at pages/PaymentMatching.jsx
  { path: "/materials", icon: Package, label: "Materials" },
  { path: "/clients", icon: Users, label: "Clients", mobileHidden: true },
  { path: "/todos", icon: ListTodo, label: "To-Do" },
  { path: "/mobile", icon: Smartphone, label: "Mobile", desktopOnly: true },
  { path: "/settings", icon: Settings, label: "Settings", adminOnly: true, mobileHidden: true },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export { useIsMobile };

export default function AppShell({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const userInitials = localStorage.getItem("desktop_user_initials") || "";
  const isAdmin = ["LH", "KH"].includes(userInitials);
  const filteredNav = NAV_ITEMS.filter(item => {
    // adminOnly items (Financials, Settings) visible only to Lucas on desktop
    if (item.adminOnly && !isAdmin) return false;
    // On mobile, hide items marked mobileHidden (Financials, Settings, Clients)
    if (isMobile && item.mobileHidden) return false;
    // desktopOnly items (Mobile link) hidden on mobile
    if (isMobile && item.desktopOnly) return false;
    return true;
  });

  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen" style={{ background: "var(--bg)" }}>
        {/* Mobile top bar */}
        <header
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 h-14"
          style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}
          data-testid="mobile-top-bar"
        >
          <div className="flex items-center gap-2">
            <img src="/hps-icon.png" alt="HPS" className="w-8 h-8 object-contain rounded" />
            <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>HPS</span>
          </div>
          <div className="flex items-center gap-2">
            <WhatsNewButton compact />
            <button
              onClick={async () => {
                // Sync desktop user session to mobile view
                const desktopId = localStorage.getItem("desktop_user_id");
                const desktopName = localStorage.getItem("desktop_user_name");
                const desktopInitials = localStorage.getItem("desktop_user_initials");
                if (desktopId) {
                  localStorage.setItem("team_member_id", desktopId);
                  localStorage.setItem("team_member_name", desktopName || "");
                  localStorage.setItem("team_member_initials", desktopInitials || "");
                  // Fetch color from team members
                  try {
                    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team-members`);
                    const members = await resp.json();
                    const me = members.find(m => m.id === desktopId);
                    if (me) localStorage.setItem("team_member_color", me.color || "#64748b");
                  } catch (e) {}
                }
                navigate("/mobile/today");
              }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: "var(--primary)", color: "#fff" }}
              data-testid="switch-to-mobile-btn"
            >
              Contractor View
            </button>
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: "var(--primary)", color: "#fff" }}>
              {userInitials || "?"}
            </div>
            <button
              onClick={() => { localStorage.removeItem("desktop_user_id"); localStorage.removeItem("desktop_user_name"); localStorage.removeItem("desktop_user_initials"); navigate("/login"); }}
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
            </button>
          </div>
        </header>

        {/* Main content with top/bottom padding for bars */}
        <main className="flex-1 pt-14 pb-16">
          {children}
        </main>

        {/* Bottom navigation */}
        <nav
          className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around h-16 border-t"
          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
          data-testid="mobile-bottom-nav"
        >
          {filteredNav.map(item => {
            const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon
                  className="w-5 h-5"
                  style={{ color: active ? "var(--primary)" : "var(--text-muted)" }}
                />
                <span
                  className="text-[10px] font-medium"
                  style={{ color: active ? "var(--primary)" : "var(--text-muted)" }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Sidebar — Light warm theme */}
      <aside
        className="fixed left-0 top-0 h-screen flex flex-col items-center py-4"
        style={{ width: 56, background: "var(--sidebar-bg)", borderRight: "1px solid var(--border)", zIndex: 40 }}
        data-testid="app-sidebar"
      >
        {/* Logo */}
        <div className="mb-6 flex items-center justify-center w-10 h-10 rounded-lg" style={{ background: "transparent" }}>
          <img src="/hps-icon.png" alt="HPS" className="w-9 h-9 object-contain rounded" />
        </div>

        {/* Nav icons */}
        <nav className="flex flex-col gap-2 flex-1">
          {filteredNav.map(item => {
            const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="group relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150"
                style={{
                  background: active ? "#FFF5F2" : "transparent",
                  borderLeft: active ? "3px solid #E05C2A" : "3px solid transparent",
                }}
                title={item.label}
                data-testid={`sidebar-${item.label.toLowerCase()}`}
              >
                <item.icon
                  style={{ width: "20px", height: "20px", color: active ? "#E05C2A" : "var(--text-muted)" }}
                />
                {/* Tooltip */}
                <span className="absolute left-14 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Current user + logout */}
        <div className="mt-auto flex flex-col items-center gap-2 pb-2">
          <WhatsNewButton />
          <div className="group relative w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: "#E05C2A", color: "#fff" }} title={localStorage.getItem("desktop_user_name") || ""}>
            {localStorage.getItem("desktop_user_initials") || "?"}
            <span className="absolute left-14 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
              {localStorage.getItem("desktop_user_name") || "User"}
            </span>
          </div>
          <button
            onClick={() => { localStorage.removeItem("desktop_user_id"); localStorage.removeItem("desktop_user_name"); localStorage.removeItem("desktop_user_initials"); navigate("/login"); }}
            className="group relative flex items-center justify-center w-10 h-10 rounded-lg transition-all hover:bg-red-500/10"
            title="Log out"
          >
            <LogOut className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          </button>
          {/* Subtle clickable version pill — surfaces the running bundle ID for ops triage. */}
          <VersionPill style={{ marginTop: 6 }} />
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1" style={{ marginLeft: 56 }}>
        <div className="flex justify-center py-4" style={{ background: "var(--bg)" }}>
          <img src="/sion-logo.png" alt="Sion Group" className="h-12 object-contain" style={{ mixBlendMode: "multiply" }} />
        </div>
        {children}
      </main>
    </div>
  );
}
